import { useEffect, useState } from "react";

import Select from "react-select";
import { toast } from "react-toastify";
import notify from "@/services/notify.tsx";
import { useForm } from "react-hook-form";
import { ServiceJornadaFinanceira } from "@/services/serviceJornadaFinanceira";
import GerarNotaFiscal from "./GerarNotaFiscal";
import moment from "moment";

interface TipoPagamento {
  id: string;
  name: string;
  is_parcial: boolean;
}

interface Parcela {
  id?: string;
  ordem: number;
  percentual: number;
  id_metodo_pagamento: string | null;
  id_conta_recebimento: string | null;
  pago: boolean;
  data_pagamento: string | null;
  pago_por: number | null;
  metodoPagamento: { id: string; name: string } | null;
  contaRecebimento: { id: string; name: string } | null;
  pagoPorUsuario: { name: string; last_name: string } | null;
}

export function Financeiro({ data, departamentoActive }: any) {
  const [loading, setLoading] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isSave, setIsSave] = useState(false);

  const [metodosPagamento, setMetodosPagamento] = useState<any>([]);
  const [contaRecebimento, setContaRecebimento] = useState<any>([]);
  const [condicaoRecebimento, setCondicaoRecebimento] = useState<any>([]);
  const [tiposPagamento, setTiposPagamento] = useState<TipoPagamento[]>([]);
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [marcandoParcela, setMarcandoParcela] = useState<string | null>(null);

  const { handleSubmit, setValue, watch } = useForm();

  const tipoPagamentoSelecionado = watch("id_tipo_pagamento") as {
    value: string;
    label: string;
    is_parcial: boolean;
  } | null;
  const isParcial = tipoPagamentoSelecionado?.is_parcial ?? false;

  const somaPercentuais = parcelas.reduce(
    (acc, p) => acc + (Number(p.percentual) || 0),
    0,
  );

  useEffect(() => {
    getFaturamento();
    getMetodosPagamento();
    getContaRecebimento();
    getCondicaoRecebimento();
    getTiposPagamento();
  }, [departamentoActive]);

  async function getTiposPagamento() {
    try {
      const res = await ServiceJornadaFinanceira.getTipoPagamento();
      if (res?.status === 200) {
        setTiposPagamento(res.data?.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function getFaturamento() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        setLoading(true);
        const res = await ServiceJornadaFinanceira.getFaturamento(data.id);
        if (res.status === 200) {
          const fat = res.data?.data;

          if (fat?.tipoPagamento) {
            setValue("id_tipo_pagamento", {
              value: fat.tipoPagamento.id,
              label: fat.tipoPagamento.name,
              is_parcial: fat.tipoPagamento.is_parcial,
            });
          }

          setValue("id_metodo_pagamento", {
            label: fat?.metodoPagamento?.name,
            value: fat?.metodoPagamento?.id,
          });
          setValue("id_conta_recebimento", {
            label: fat?.contaRecebimento?.name,
            value: fat?.contaRecebimento?.id,
          });
          setValue("id_condicao_recebimento", {
            label: fat?.condicoesRecebimento?.name,
            value: fat?.condicoesRecebimento?.id,
          });

          if (Array.isArray(fat?.parcelas) && fat.parcelas.length > 0) {
            setParcelas(
              fat.parcelas.map((p: any) => ({
                id: p.id,
                ordem: p.ordem,
                percentual: parseFloat(p.percentual),
                id_metodo_pagamento: p.id_metodo_pagamento,
                id_conta_recebimento: p.id_conta_recebimento,
                pago: p.pago,
                data_pagamento: p.data_pagamento,
                pago_por: p.pago_por,
                metodoPagamento: p.metodoPagamento,
                contaRecebimento: p.contaRecebimento,
                pagoPorUsuario: p.pagoPorUsuario,
              })),
            );
          }

          const isLegacySave =
            !fat?.tipoPagamento &&
            fat?.metodoPagamento?.name &&
            fat?.contaRecebimento?.name &&
            fat?.condicoesRecebimento?.name;

          if (isLegacySave) setIsSave(true);
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  function adicionarParcela() {
    setParcelas((prev) => [
      ...prev,
      {
        ordem: prev.length + 1,
        percentual: 0,
        id_metodo_pagamento: null,
        id_conta_recebimento: null,
        pago: false,
        data_pagamento: null,
        pago_por: null,
        metodoPagamento: null,
        contaRecebimento: null,
        pagoPorUsuario: null,
      },
    ]);
  }

  function removerParcela(index: number) {
    setParcelas((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((p, i) => ({ ...p, ordem: i + 1 })),
    );
  }

  function atualizarParcela(index: number, campo: keyof Parcela, valor: any) {
    setParcelas((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [campo]: valor } : p)),
    );
  }

  async function handleMarcarPaga(parcela: Parcela, index: number) {
    if (!parcela.id) return;
    setMarcandoParcela(parcela.id);
    try {
      const res = await ServiceJornadaFinanceira.marcarParcelaPaga(parcela.id, {
        pago: !parcela.pago,
      });
      if (res?.status === 200) {
        setParcelas((prev) =>
          prev.map((p, i) =>
            i === index
              ? {
                  ...p,
                  pago: res.data.data.pago,
                  data_pagamento: res.data.data.data_pagamento,
                  pago_por: res.data.data.pago_por,
                  pagoPorUsuario: p.pagoPorUsuario,
                }
              : p,
          ),
        );
        notify.update(
          null as any,
          "success",
          res.data.data.pago
            ? "Parcela marcada como paga!"
            : "Marcação desfeita!",
        );
      }
    } catch (error) {
      toast.error("Erro ao marcar parcela");
    } finally {
      setMarcandoParcela(null);
    }
  }

  const onSubmit = async (payload: any) => {
    if (isParcial && somaPercentuais !== 100) {
      toast.error(
        `A soma dos percentuais deve ser 100%. Atual: ${somaPercentuais}%`,
      );
      return;
    }

    const toastId = notify.loading("Salvando dados financeiros...");
    try {
      setIsSubmiting(true);

      payload.id_tipo_pagamento = payload?.id_tipo_pagamento?.value ?? null;
      payload.id_condicao_recebimento =
        payload?.id_condicao_recebimento?.value ?? null;

      if (isParcial) {
        delete payload.id_metodo_pagamento;
        delete payload.id_conta_recebimento;
        payload.parcelas = parcelas.map((p) => ({
          ordem: p.ordem,
          percentual: Number(p.percentual),
          id_metodo_pagamento: p.id_metodo_pagamento,
          id_conta_recebimento: p.id_conta_recebimento,
        }));
      } else {
        payload.id_metodo_pagamento =
          payload?.id_metodo_pagamento?.value ?? null;
        payload.id_conta_recebimento =
          payload?.id_conta_recebimento?.value ?? null;
        delete payload.parcelas;
      }

      const res = await ServiceJornadaFinanceira.addFaturamento(
        data.id,
        payload,
      );

      if (res.status === 200) {
        getFaturamento();
        notify.update(toastId, "success", "Adicionado com sucesso!");
      }
    } catch (error: any) {
      notify.update(
        toastId,
        "error",
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Erro ao salvar financeiro",
      );
    } finally {
      setIsSubmiting(false);
    }
  };

  async function getMetodosPagamento() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        setLoading(true);
        const res = await ServiceJornadaFinanceira.getMetodosPagamento();
        if (res.status === 200) {
          setMetodosPagamento(res.data.data);
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }
  async function getContaRecebimento() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        setLoading(true);
        const res = await ServiceJornadaFinanceira.getContaRecebimento();
        if (res.status === 200) {
          setContaRecebimento(res.data.data);
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }
  async function getCondicaoRecebimento() {
    try {
      const { token } = JSON.parse(localStorage.getItem("token") || "{}");
      if (token) {
        setLoading(true);
        const res = await ServiceJornadaFinanceira.getCondicaoRecebimento();
        if (res.status === 200) {
          setCondicaoRecebimento(res.data.data);
        }
      }
    } catch (error) {
      toast.error("Não foi possível buscar!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const opcoesMetodo =
    metodosPagamento?.map((m: any) => ({ value: m.id, label: m.name })) || [];
  const opcoesConta =
    contaRecebimento?.map((c: any) => ({ value: c.id, label: c.name })) || [];

  return (
    <>
      <div>
        <div>
          {loading &&
            Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="bg-[#EDEDED] rounded-3xl px-8 py-4 h-16"
              >
                <div className="flex justify-between cursor-pointer">
                  <h2 className="font-medium bg-gray-300 h-6 w-40"></h2>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="bg-gray-300 h-4 w-20 rounded-2xl"></span>
                    <span className="bg-white px-4 py-1 rounded-2xl font-medium w-24"></span>
                    <button
                      type="button"
                      className="bg-gray-300 h-5 w-5 rounded-full"
                    ></button>
                  </div>
                </div>
              </div>
            ))}
          <GerarNotaFiscal
            id_desk={data.id}
            ref_nfe={data.ref_nfe}
            status_nfe={data.status_nfe}
            variant="icon"
            deskData={data}
          />
          <form
            className="bg-white rounded-3xl flex flex-col gap-3 max-w-2xl w-full z-20 relative mx-auto"
            onSubmit={handleSubmit(onSubmit)}
          >
            <h2 className="text-lg text-center font-medium text-cinza-800">
              Informações de faturamento
            </h2>

            {/* Tipo de Pagamento */}
            <Select
              onChange={(value) => {
                setValue("id_tipo_pagamento", value);
                setParcelas([]);
              }}
              options={tiposPagamento.map((t) => ({
                value: t.id,
                label: t.name,
                is_parcial: t.is_parcial,
              }))}
              value={watch("id_tipo_pagamento") || null}
              placeholder="Tipo de pagamento"
              isClearable
            />

            {/* Condição de recebimento — sempre visível */}
            <Select
              onChange={(value) => setValue("id_condicao_recebimento", value)}
              options={
                condicaoRecebimento?.map((c: any) => ({
                  value: c.id,
                  label: c.name,
                })) || []
              }
              value={watch("id_condicao_recebimento")}
              placeholder="Selecione a condição de recebimento"
            />

            {/* À vista: método + conta no nível raiz */}
            {!isParcial && (
              <>
                <Select
                  onChange={(value) => setValue("id_metodo_pagamento", value)}
                  options={opcoesMetodo}
                  isDisabled={isSave}
                  value={watch("id_metodo_pagamento")}
                  placeholder="Selecione um método de pagamento"
                />
                <Select
                  onChange={(value) => setValue("id_conta_recebimento", value)}
                  options={opcoesConta}
                  isDisabled={isSave}
                  value={watch("id_conta_recebimento")}
                  placeholder="Conta de recebimento"
                />
              </>
            )}

            {/* Parcial: tabela de parcelas */}
            {isParcial && (
              <div className="flex flex-col gap-2">
                {parcelas.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {parcelas.map((parcela, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-cinza-400 w-14 shrink-0">
                            Parcela {index + 1}
                          </span>
                          <div className="flex items-center gap-1 w-24 shrink-0">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={parcela.percentual}
                              onChange={(e) =>
                                atualizarParcela(
                                  index,
                                  "percentual",
                                  e.target.value,
                                )
                              }
                              className="w-16 border border-gray-200 rounded px-2 py-1 text-sm text-center"
                            />
                            <span className="text-sm text-cinza-400">%</span>
                          </div>
                          <div className="flex-1">
                            <Select
                              onChange={(value: any) =>
                                atualizarParcela(
                                  index,
                                  "id_metodo_pagamento",
                                  value?.value ?? null,
                                )
                              }
                              options={opcoesMetodo}
                              value={
                                opcoesMetodo.find(
                                  (o: any) =>
                                    o.value === parcela.id_metodo_pagamento,
                                ) ||
                                (parcela.metodoPagamento
                                  ? {
                                      value: parcela.id_metodo_pagamento,
                                      label: parcela.metodoPagamento.name,
                                    }
                                  : null)
                              }
                              placeholder="Método"
                              isClearable
                            />
                          </div>
                          <div className="flex-1">
                            <Select
                              onChange={(value: any) =>
                                atualizarParcela(
                                  index,
                                  "id_conta_recebimento",
                                  value?.value ?? null,
                                )
                              }
                              options={opcoesConta}
                              value={
                                opcoesConta.find(
                                  (o: any) =>
                                    o.value === parcela.id_conta_recebimento,
                                ) ||
                                (parcela.contaRecebimento
                                  ? {
                                      value: parcela.id_conta_recebimento,
                                      label: parcela.contaRecebimento.name,
                                    }
                                  : null)
                              }
                              placeholder="Conta"
                              isClearable
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removerParcela(index)}
                            className="text-red-400 hover:text-red-600 text-xs px-1"
                            title="Remover parcela"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Marcar como pago */}
                        {parcela.id && (
                          <div className="flex items-center gap-2 pl-16">
                            {parcela.pago ? (
                              <span className="text-xs text-verde-400">
                                ✓ Pago por {parcela.pagoPorUsuario?.name}{" "}
                                {parcela.pagoPorUsuario?.last_name} em{" "}
                                {moment(parcela.data_pagamento).format(
                                  "DD/MM/YYYY HH:mm",
                                )}
                              </span>
                            ) : null}
                            <button
                              type="button"
                              disabled={marcandoParcela === parcela.id}
                              onClick={() => handleMarcarPaga(parcela, index)}
                              className={`text-xs px-3 py-1 rounded-md border transition-colors ${
                                parcela.pago
                                  ? "border-red-300 text-red-500 hover:bg-red-50"
                                  : "border-verde-300 text-verde-500 hover:bg-verde-50"
                              } disabled:opacity-50`}
                            >
                              {marcandoParcela === parcela.id
                                ? "..."
                                : parcela.pago
                                  ? "Desfazer"
                                  : "Marcar como pago"}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={adicionarParcela}
                    className="text-sm text-azul-400 hover:underline"
                  >
                    + Adicionar parcela
                  </button>
                  <span
                    className={`text-sm font-medium ${
                      somaPercentuais === 100
                        ? "text-verde-400"
                        : "text-red-400"
                    }`}
                  >
                    Total: {somaPercentuais}%
                  </span>
                </div>
              </div>
            )}

            <button
              className={`py-1 rounded-md text-white w-full mx-auto ${
                isSave && !isParcial ? "bg-verde-300" : "bg-azul-400"
              }`}
              type="submit"
              disabled={isSubmiting}
            >
              {isSubmiting
                ? "SALVANDO"
                : isSave && !isParcial
                  ? "SALVO"
                  : "SALVAR"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
